/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import {
  Component,
  OnInit,
  Input,
  Self,
  Optional,
  ElementRef,
  ViewChild,
  EventEmitter,
  Output,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { XdaStringsService } from '../../services/xda-strings.service';
import { XdaTranslate } from '../../pipes/xda-translate.pipe';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BasicAlertComponent } from '../alerts/basic-alert.component';

@Component({
  selector: 'xrx-multi-file-select',
  templateUrl: './xrx-multi-file-select.component.html',
  styleUrls: ['./xrx-multi-file-select.component.less'],
  standalone: false,
})
export class XrxMultiFileSelectComponent
  implements OnInit, ControlValueAccessor
{
  @Input() disabled: boolean;
  @Input() maxFiles = '1';
  @Input() minFiles = '1';
  @Input() mode: 'single' | 'batch' | 'merge' = 'single';
  @Input() name = '';
  @Input() id: string;
  @Input() sortable = false;
  @Input() required: boolean;
  @Input() fileTypesAccepted: string;
  @Input() fileTypes = '';
  @Input() maxFilesSelectedError = 'SDE_MAXIMUM_FMTSTR_FILES';

  @Output() fileDropped = new EventEmitter<object>();

  @ViewChild('uploadFilesInput') uploadFilesInput: ElementRef<HTMLElement>;

  fileDragOver = false;
  fileFormatNotSupported = false;
  specialCharactersInFileName = false;
  fileInputText: string;
  input: any = {
    uploadFiles: [],
    fileTypes: {
      asAccept: '',
    },
  };
  isTruncated: boolean;
  isValid = true;
  maxFilesExceeded = false;
  minFilesExceeded = false;
  requiredField = false;
  numberFileRequired = false;
  multiple = false;

  private xdaTranslatePipe: XdaTranslate;
  constructor(
    @Self() @Optional() public ngControl: NgControl,
    private xdaStringsService: XdaStringsService,
    private modalService: NgbModal,
  ) {
    this.ngControl.valueAccessor = this;
    this.xdaTranslatePipe = new XdaTranslate(this.xdaStringsService);
    this.fileInputText = this.xdaTranslatePipe.transform('SDE_DROP_FILE_HERE1');
  }

  ngOnInit() {
    if (this.mode == 'batch' || this.mode == 'merge') {
      this.multiple = true;
    }
    if (!this.multiple && this.input.uploadFiles.length > 0) {
      this.fileInputText = this.xdaTranslatePipe.transform(
        this.input.uploadFiles[0].name,
      );
    }
  }

  // file drag start callback
  fileDragStart(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.fileDragOver = true;
  }
  // file end start callback
  fileDragEnd(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    if ((event.target as Element).classList.contains('file-drag-overlay')) {
      this.fileDragOver = false;
    }
  }
  // file drop start callback
  fileDragDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.fileDragOver = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleUploadFilesInput(files);
    }
  }

  handleUploadFilesInput(files: FileList | null) {
    const maxFiles = parseInt(this.maxFiles);
    this.resetErrors();

    if (files) {
      const fileArr = Array.from(files);

      if (this.multiple) {
        if (this.input.uploadFiles.length + fileArr.length <= maxFiles) {
          this.input.uploadFiles.push(...fileArr);
          this.validateInput();
        } else {
          this.errorAlert(
            this.xdaTranslatePipe.transform(
              this.maxFilesSelectedError,
              this.maxFiles,
            ),
          );
        }
      } else {
        if (fileArr.length < 2) {
          this.input.uploadFiles = fileArr;
          this.fileInputText = this.xdaTranslatePipe.transform(
            this.input.uploadFiles[0].name,
          );
          this.validateInput();
        } else {
          this.errorAlert(
            this.xdaTranslatePipe.transform(
              this.maxFilesSelectedError,
              this.maxFiles,
            ),
          );
        }
      }
    }

    this.fileDropped.emit({
      files: this.input.uploadFiles,
      isValid: this.isValid,
    });
  }

  validateFileNames(file: File) {
    let fileIsValid = true;

    if (!file.name) {
      this.ngControl.control?.markAsTouched();
    }

    // check to see if the file has a file extension
    const index = file.name.lastIndexOf('.');
    if (index === -1) {
      fileIsValid = false;
    }

    // check to see if the file is one that the input accepts
    const fileFormat = file.name.substr(index).toLowerCase();
    if (this.fileTypesAccepted.split(',').indexOf(fileFormat) === -1) {
      fileIsValid = false;
    }

    return fileIsValid;
  }

  validateSpecialCharacters(file: File) {
    return /^[^"?;<>|:'*%^&#+`\\\/]+$/.test(file.name);
  }

  errorAlert(title: string) {
    const modalRef = this.modalService.open(BasicAlertComponent);
    modalRef.componentInstance.title = title;
    modalRef.result.then((data) => {
      return;
    });
  }

  reorder(index: number, direction: string) {
    const to = direction == 'up' ? index - 1 : index + 1;
    this.input.uploadFiles.splice(
      index,
      0,
      this.input.uploadFiles.splice(to, 1)[0],
    );
    this.fileDropped.emit({
      files: this.input.uploadFiles,
      isValid: this.isValid,
    });
  }

  delete(event: Event, index: number) {
    const maxFiles = parseInt(this.maxFiles);
    event.preventDefault();
    event.stopPropagation();
    this.input.uploadFiles.splice(index, 1);

    this.resetErrors();
    this.ngControl.control?.markAsTouched();

    if (this.input.uploadFiles.length < 1) {
      this.fileInputText = this.xdaTranslatePipe.transform(
        'SDE_DROP_FILE_HERE1',
      );
    } else {
      this.validateInput();
    }

    this.fileDropped.emit({
      files: this.input.uploadFiles,
      isValid: this.isValid,
    });
  }

  checkTrucation(event: Event, t: any) {
    if (
      (event.target as HTMLElement).scrollWidth >
      (event.target as HTMLElement).offsetWidth
    ) {
      // open tooltip
      t.open();
    } else if (this.mode == 'single') {
      // open native browse window
      this.uploadFilesInput.nativeElement.click();
    }
  }

  validateInput() {
    if (!this.isValid) {
      return this.isValid;
    }

    this.isValid = false;
    if (
      (this.mode === 'single' || this.mode === 'batch') &&
      this.input.uploadFiles.length === 0
    ) {
      this.requiredField = true;
    } else if (
      this.maxFiles === this.minFiles &&
      this.input.uploadFiles.length < this.maxFiles
    ) {
      this.numberFileRequired = true;
    } else if (this.input.uploadFiles.length < this.minFiles) {
      this.minFilesExceeded = true;
    } else {
      this.isValid = true;
    }

    // check for valid file names
    this.fileFormatNotSupported = false;
    for (let i = 0; i < this.input.uploadFiles.length; i++) {
      if (!this.validateFileNames(this.input.uploadFiles[i])) {
        this.fileFormatNotSupported = true;
        this.isValid = false;
        break;
      }
    }

    this.specialCharactersInFileName = false;
    // check for invalid characters in file names if multiple files have been selected
    if (this.input.uploadFiles.length > 1) {
      for (let i = 0; i < this.input.uploadFiles.length; i++) {
        if (!this.validateSpecialCharacters(this.input.uploadFiles[i])) {
          this.specialCharactersInFileName = true;
          this.isValid = false;
          break;
        }
      }
    }

    return this.isValid;
  }

  // update value of input.uploadFile when component is loaded
  writeValue(obj: any): void {
    // setTimeout because somewhere during initilization the default value is being erased
    setTimeout(() => {
      if (obj != null) {
        this.input.uploadFiles = obj;
      }
    });
  }

  // reset field function
  resetInputSettings() {
    this.input.uploadFileS = [];
    this.resetErrors();

    setTimeout(() => {
      const control = this.ngControl.control;
      control?.markAsUntouched();
      control?.markAsPristine();
      control?.updateValueAndValidity();
    }, 1);
  }

  resetErrors() {
    this.fileFormatNotSupported = false;
    this.specialCharactersInFileName = false;
    this.isValid = true;
    this.maxFilesExceeded = false;
    this.minFilesExceeded = false;
    this.requiredField = false;
    this.numberFileRequired = false;
  }

  // function needed for ControlValueAccessor
  registerOnChange(fn: any): void {}

  // function needed for ControlValueAccessor
  registerOnTouched(fn: any): void {}
}
